import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@smena/ui';
import { useLogin } from './hooks';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginMut = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await loginMut.mutateAsync({ email, password, code });
      const next = new URLSearchParams(location.search).get('next') ?? '/';
      navigate(next, { replace: true });
    } catch (err) {
      // Сервер намеренно не различает причины (email / пароль / код / rate-limit) - и мы не различаем.
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Не сходятся email, пароль или код'
          : 'Ошибка входа, попробуйте ещё раз',
      );
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-5 p-6">
      <div className="flex items-center gap-2.5 text-lg font-bold tracking-tight">
        <i className="size-2.5 rotate-45 bg-accent shadow-[0_0_0_4px_rgba(109,40,217,.24)]" />
        <span>Smena</span>
        <span className="rounded-sm bg-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          admin
        </span>
      </div>
      <Card className="w-full max-w-sm shadow-pop">
        <CardHeader>
          <CardTitle className="text-lg">Внутренняя панель</CardTitle>
          <CardDescription>
            Внутренний инструмент вендора. Учётки заводятся только через CLI, вход - с кодом
            из аутентификатора.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} autoComplete="on" noValidate className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="code">Код из аутентификатора</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="000000"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono tracking-[0.35em]"
              />
            </div>
            <Button type="submit" size="lg" disabled={loginMut.isPending} className="mt-3">
              {loginMut.isPending ? 'Проверяем…' : 'Войти'}
            </Button>
            {error ? (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            ) : null}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
